import i18n from "i18next";
import { mapValues } from "lodash-es";
import { initReactI18next } from "react-i18next";

import en from "./en.json";
import zhCN from "./zh-CN.json";
import zhTW from "./zh-TW.json";
import jaJP from "./ja-JP.json";
import koKR from "./ko-KR.json";
import fr from "./fr.json";
import de from "./de.json";
import it from "./it.json";
import es from "./es.json";
import ptPT from "./pt-PT.json";
import nl from "./nl.json";
import uk from "./uk.json";
import pl from "./pl.json";

export const catalogs = {
  en,
  "zh-CN": zhCN,
  "zh-TW": zhTW,
  "ja-JP": jaJP,
  "ko-KR": koKR,
  fr,
  de,
  it,
  es,
  "pt-PT": ptPT,
  nl,
  uk,
  pl,
};

export type UiLocale = keyof typeof catalogs;
export const supportedLocales = Object.keys(catalogs) as UiLocale[];
export const resources = mapValues(catalogs, (translation) => ({ translation }));
export const defaultLocale: UiLocale = "en";
export const uiLocaleStorageKey = "blycare-admin-locale";

export const t = i18n.t.bind(i18n);

// Admin UI language is independent of the widget's content editing locale.
// Empty catalogs intentionally fall back to English until translations are added.
export async function initI18n() {
  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      resources,
      lng: readStoredLocale(),
      fallbackLng: defaultLocale,
      supportedLngs: supportedLocales,
      load: "currentOnly",
      ns: ["translation"],
      defaultNS: "translation",
      keySeparator: false,
      nsSeparator: false,
      enableSelector: false,
      returnEmptyString: false,
      interpolation: { escapeValue: false },
    });
  }
  return i18n;
}

export async function changeUiLocale(locale: UiLocale) {
  try {
    window.localStorage.setItem(uiLocaleStorageKey, locale);
  } catch {
    // Language switching still works when storage is unavailable.
  }
  await i18n.changeLanguage(locale);
}

function readStoredLocale(): UiLocale {
  try {
    const locale = window.localStorage.getItem(uiLocaleStorageKey);
    return supportedLocales.includes(locale as UiLocale) ? (locale as UiLocale) : defaultLocale;
  } catch {
    return defaultLocale;
  }
}

export default i18n;
